import React, { useMemo, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const ActivityChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    // Calcular total e percentuais
    const { activities, total } = useMemo(() => {
        const t = data.reduce((sum, a) => sum + (a.count || 0), 0);
        const items = [...data].map(a => ({
            ...a,
            percentage: t > 0 ? ((a.count / t) * 100).toFixed(1) : "0.0"
        })).sort((a, b) => b.count - a.count);
        return { activities: items, total: t };
    }, [data]);

    // Sem dados
    if (data.length === 0) {
        return (
            <Card 
                className="neo-stat-card h-100" 
                style={{ 
                    borderRadius: "16px", 
                    border: "2px solid #482712", 
                    boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                    background: "#fff",
                    fontFamily: "'Outfit', 'Inter', sans-serif"
                }}
            >
                <CardHeader className="bg-transparent border-0 pt-4 px-4 pb-0">
                    <h6 className="text-uppercase fw-bold m-0" style={{ fontSize: "11px", letterSpacing: "1px", color: "#482712" }}>Alunos por Atividade</h6>
                </CardHeader>
                <CardBody className="p-4 d-flex align-items-center justify-content-center" style={{ minHeight: "250px" }}>
                    <div className="text-center">
                        <i className="mdi mdi-chart-bar mdi-48px mb-2 d-block" style={{ color: "#fef5ef" }}></i>
                        <p className="mb-0 fw-semibold" style={{ fontSize: "13px", color: "#784b22" }}>Nenhuma matrícula registrada</p>
                    </div>
                </CardBody>
            </Card>
        );
    }

    // Retorna a cor sólida do ranking para visual limpo sem degradê
    const getBarColor = (index) => {
        if (index === 0) return "#e2661c"; // Primeiro: Laranja de Destaque
        if (index === 1) return "#c49a6c"; // Segundo: Ouro/Amber
        if (index === 2) return "#784b22"; // Terceiro: Marrom Clássico
        return "#c3b3a3"; // Outros: Bege/Marrom suave
    };

    // Helper inteligente para abreviar nomes das atividades e caberem perfeitamente na horizontal
    const abbreviateName = (name) => {
        const lower = name.toLowerCase();
        if (lower.includes("hidroginástica") || lower.includes("hidro")) return "Hidro";
        if (lower.includes("natação adulto") || lower.includes("natacao adulto")) return "Nat. Ad.";
        if (lower.includes("natação") || lower.includes("natacao")) return "Nat.";
        if (lower.includes("estágio") || lower.includes("estagio")) {
            const num = name.match(/\d+/);
            return num ? `Est. ${num[0]}` : "Estágio";
        }
        if (lower.includes("baby")) {
            const num = name.match(/\d+/);
            return num ? `Baby ${num[0]}` : "Baby";
        }
        if (name.length > 8) {
            return name.substring(0, 6) + ".";
        }
        return name;
    };

    return (
        <Card 
            className="neo-stat-card h-100" 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="neo-chart-card-header">
                <div className="neo-chart-header-container">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{ 
                                width: "38px", 
                                height: "38px", 
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-chart-bar font-size-18"></i>
                        </div>
                        <h5 className="neo-chart-title">
                            Alunos por Atividade
                        </h5>
                    </div>
                    <span 
                        style={{ 
                            fontSize: "0.78rem", 
                            color: "#784b22", 
                            fontWeight: "bold", 
                            background: "#fef5ef", 
                            padding: "4px 12px", 
                            borderRadius: "20px",
                            border: "1px solid rgba(72, 39, 18, 0.15)"
                        }}
                    >
                        {total} alunos
                    </span>
                </div>
            </CardHeader>
            <CardBody className="p-4 mt-2">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "260px", display: "flex", alignItems: "flex-end" }}>
                        {/* ÁREA DOS GRÁFICOS DE BARRAS VERTICAIS */}
                        <div 
                            style={{ 
                                height: "200px", 
                                display: "flex", 
                                alignItems: "flex-end", 
                                justifyContent: "space-between", 
                                gap: "6px", 
                                borderBottom: "none",
                                position: "relative",
                                minWidth: "550px", // Evita esmagamento no mobile
                                width: "100%"
                            }}
                        >
                        {activities.map((item, idx) => {
                            const isHovered = hoveredIdx === idx;
                            const isFirst = idx === 0;

                            return (
                                <div 
                                    key={idx} 
                                    style={{ 
                                        flex: 1, 
                                        display: "flex", 
                                        flexDirection: "column", 
                                        alignItems: "center", 
                                        height: "100%",
                                        justifyContent: "flex-end",
                                        position: "relative"
                                    }}
                                    onMouseEnter={() => setHoveredIdx(idx)}
                                    onMouseLeave={() => setHoveredIdx(null)}
                                >
                                    {/* Tooltip flutuante de valor */}
                                    <div 
                                        style={{ 
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            marginBottom: "6px",
                                            transition: "transform 0.2s",
                                            transform: isHovered ? "scale(1.1)" : "scale(1)"
                                        }}
                                    >
                                        <span style={{ fontSize: "0.78rem", fontWeight: "900", color: "#482712" }}>
                                            {item.count}
                                        </span>
                                        <span style={{ fontSize: "0.68rem", fontWeight: "800", color: isFirst ? "#e2661c" : "#784b22" }}>
                                            {item.percentage}%
                                        </span>
                                    </div>

                                    {/* Trilho/Guia da barra vertical */}
                                    <div 
                                        style={{ 
                                            width: "100%",
                                            maxWidth: "34px",
                                            height: "120px", 
                                            backgroundColor: "#fef5ef", 
                                            border: isHovered ? "2px solid #e2661c" : "1.5px solid rgba(72, 39, 18, 0.15)", 
                                            borderRadius: "8px", 
                                            display: "flex", 
                                            flexDirection: "column", 
                                            justifyContent: "flex-end", 
                                            overflow: "hidden",
                                            boxShadow: isHovered ? "0 4px 12px rgba(226, 102, 28, 0.15)" : "none",
                                            transition: "all 0.25s ease"
                                        }}
                                    >
                                        {/* Preenchimento da barra vertical */}
                                        <div 
                                            style={{ 
                                                width: "100%", 
                                                height: `${item.percentage}%`, 
                                                background: getBarColor(idx),
                                                borderRadius: "0 0 4px 4px",
                                                transition: "height 0.8s cubic-bezier(0.16, 1, 0.3, 1)"
                                            }}
                                        />
                                    </div>

                                    {/* Nomes das Atividades Abreviados de Forma Limpa na Horizontal */}
                                    <span 
                                        style={{ 
                                            marginTop: "8px", 
                                            fontSize: "0.68rem", 
                                            fontWeight: "800", 
                                            color: isHovered ? "#e2661c" : "#482712",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                            transition: "color 0.2s",
                                            lineHeight: "1"
                                        }}
                                        title={item.name} // Nome completo continua acessível no hover do navegador
                                    >
                                        {abbreviateName(item.name)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            </CardBody>
        </Card>
    );
};

export default ActivityChart;
